import React, { useState } from "react";
import { useUser } from "util/UserContext";
import { Drawer, DrawerContent } from "ui/Drawer";
import Modal from "ui/Modal";
import { HeartHandshake, UserMinus } from "lucide-react";
import api from "config/axiosConfig";
import { toast } from "sonner";
import DialogConfirmation from "ui/DialogConfirmation";

interface FollowDetailsProps {
  /**
   * Whether the details modal/drawer is open
   */
  isOpen: boolean;
  
  /**
   * Function to close the details modal/drawer
   */
  onClose: () => void;
  
  /**
   * The ID of the entity being followed
   */
  entityId: number;
  
  /**
   * The type of entity being followed (user or team)
   */
  entityType: "user" | "team";
  
  /**
   * The name of the entity being followed
   */
  entityName: string;
  
  /**
   * Whether the entity is already a bestea
   */
  isBestea: boolean;
  
  /**
   * Function called when bestea status changes
   */
  onBesteaChange?: (isBestea: boolean) => void;
  
  /**
   * Function called when the user unfollows
   */
  onUnfollow?: () => void;
  
  /**
   * Whether the entity is private
   */
  isPrivate?: boolean;
}

/**
 * A component for displaying detailed follow actions (bestea, unfollow)
 * Renders either in a modal or drawer based on device type
 */
const FollowDetails: React.FC<FollowDetailsProps> = ({
  isOpen,
  onClose,
  entityId,
  entityType,
  entityName,
  isBestea,
  onBesteaChange,
  onUnfollow,
  isPrivate = false,
}) => {
  const { userIsMobile } = useUser();
  const [showUnfollowConfirmation, setShowUnfollowConfirmation] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * Toggle the bestea status of an entity
   */
  const handleBesteaToggle = async () => {
    try {
      setIsUpdating(true);
      // Use status 'add' or 'remove' based on current status
      const status = isBestea ? false : true;
      
      await api.patch(`/api/follow/bestea/${entityId}/${status}`);
      
      // Update local state via callback
      onBesteaChange?.(!isBestea);
      
      toast.success(isBestea 
        ? `Removed ${entityName} from your besteas` 
        : `Added ${entityName} to your besteas`
      );
    } catch (error) {
      console.error("Error updating bestea status:", error);
      toast.error("Failed to update bestea status");
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Handle unfollow button click
   */
  const handleUnfollowClick = () => {
    if (isPrivate) {
      setShowUnfollowConfirmation(true);
    } else {
      handleUnfollow();
    }
  };

  /**
   * Unfollow the entity
   */
  const handleUnfollow = async () => {
    try {
      setIsUpdating(true);
      
      await api.delete("/api/follow", {
        data: {
          followingId: entityId,
          followingType: entityType,
        },
      });
      
      toast.success(`Unfollowed ${entityName}`);
      
      // Close the modal/drawer and call the callback
      onClose();
      onUnfollow?.();
    } catch (error) {
      console.error("Error unfollowing:", error);
      toast.error("Failed to unfollow");
    } finally {
      setIsUpdating(false);
      setShowUnfollowConfirmation(false);
    }
  };

  // Content to display in either drawer or modal
  const content = (
    <div className="flex flex-col gap-4 p-4">
      <button
        onClick={handleBesteaToggle}
        disabled={isUpdating}
        className="flex items-center gap-2 p-3 rounded-lg text-left hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover"
      >
        <HeartHandshake className="size-5 text-eoe-text dark:text-eoe-dark-text" />
        <span className="font-medium text-eoe-text dark:text-eoe-dark-text">
          {isBestea ? "Remove from besteas" : "Add to besteas"}
        </span>
      </button>

      <button
        onClick={handleUnfollowClick}
        disabled={isUpdating}
        className="flex items-center gap-2 p-3 rounded-lg text-left text-eoe-red dark:text-eoe-red hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover"
      >
        <UserMinus className="size-5" />
        <span className="font-medium">Unfollow</span>
      </button>
    </div>
  );

  // Show confirmation dialog for unfollowing private entities
  const confirmationDialog = showUnfollowConfirmation && (
    <DialogConfirmation
      isOpen={showUnfollowConfirmation}
      title={`Unfollow ${entityType}?`}
      description={`${entityType === "user" ? "User" : "Partea"} ${entityName} is private, and you will have to request to follow them again.`}
      onConfirm={handleUnfollow}
      onCancel={() => setShowUnfollowConfirmation(false)}
      confirmText="Unfollow"
      cancelText="Cancel"
    />
  );

  // Render drawer for mobile devices
  if (userIsMobile) {
    return (
      <>
        <Drawer open={isOpen} onOpenChange={onClose}>
          <DrawerContent title="Follow options">
            {content}
          </DrawerContent>
        </Drawer>
        {confirmationDialog}
      </>
    );
  }

  // Render modal for desktop
  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Follow options"
        className="max-w-sm"
      >
        {content}
      </Modal>
      {confirmationDialog}
    </>
  );
};

export default FollowDetails;
